import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCustomers } from '../../context/CustomerContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';
import Breadcrumb from '../common/Breadcrumb';
import {
  UserIcon,
  MapPinIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  PencilIcon,
  TagIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  XCircleIcon,
  PhotoIcon,
  EyeIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

const CustomerView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { customers, getCustomer, getCustomers, getCustomerAdditionalData } = useCustomers();
  const { hideLoading } = useLoading();
  const getRoutePath = useRoutePath();

  const [customer, setCustomer] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [customerTags, setCustomerTags] = useState([]);
  const [referralSources, setReferralSources] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [assignedContracts, setAssignedContracts] = useState([]);
  const [taxItems, setTaxItems] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [assignedAgents, setAssignedAgents] = useState([]);
  const [prefixes, setPrefixes] = useState([]);
  const [frequencies, setFrequencies] = useState([]);

  const hasFetchedCustomer = useRef(false);
  const hasFetchedAdditional = useRef(false);

  const fetchCustomer = useCallback(
    async () => {
      if (!id || hasFetchedCustomer.current) return;

      hasFetchedCustomer.current = true;
      setIsFetching(true);

      try {
        // Try to get from already loaded context first
        let found = getCustomer(id);

        // If not found (e.g. direct URL hit), load all customers once
        if (!found) {
          const response = await getCustomers(1, { all: true });

          if (response?.data) {
            const list = response.data;
            if (Array.isArray(list)) {
              found = list.find(item => Number(item.id) === Number(id));
            }
          }
        }

        if (found) {
          setCustomer(found);
        } else {
          setCustomer(null);
        }
      } catch (error) {
        console.error('Error fetching customer data:', error);
      } finally {
        setIsFetching(false);
        hideLoading();
      }
    },
    [id, getCustomer, getCustomers, hideLoading]
  );

  const fetchAdditionalData = useCallback(
    async () => {
      if (hasFetchedAdditional.current) return;
      hasFetchedAdditional.current = true;

      try {
        const response = await getCustomerAdditionalData();
        if (response?.customerTags) {
          setCustomerTags(response.customerTags.data || []);
        }
        if (response?.referralSources) {
          setReferralSources(response.referralSources || []);
        }
        if (response?.industries) {
          setIndustries(response.industries || []);
        }
        if (response?.assignedContracts) {
          setAssignedContracts(response.assignedContracts || []);
        }
        if (response?.taxItems) {
          setTaxItems(response.taxItems || []);
        }
        if (response?.currencies) {
          setCurrencies(response.currencies || []);
        }
        if (response?.assignedAgents) {
          setAssignedAgents(response.assignedAgents?.data || []);
        }
        if (response?.primaryContractPrefixes) {
          setPrefixes(response.primaryContractPrefixes || []);
        }
        if (response?.sAgreementFrequencies) {
          setFrequencies(response.sAgreementFrequencies || []);
        }
      } catch (error) {
        console.error('Error fetching customer additional data:', error);
      }
    },
    [getCustomerAdditionalData]
  );

  useEffect(() => {
    // If list is already in context we might be able to resolve quickly
    if (id && customers?.data?.length && !customer) {
      const existing = customers.data.find(item => Number(item.id) === Number(id));
      if (existing) {
        setCustomer(existing);
        setIsFetching(false);
        return;
      }
    }

    fetchCustomer();
    fetchAdditionalData();
  }, [id, customers, customer, fetchCustomer, fetchAdditionalData]);

  const handleEdit = () => {
    navigate(getRoutePath(`/customers/edit/${id}`));
  };

  const handleViewFile = (url) => {
    if (!url) return;
    window.open(url, '_blank');
  };

  const handleDownloadFile = (url, filename) => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.download = filename || 'file';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getCustomerStatusBadge = () => {
    const isActive = customer?.status === 1 || customer?.active;

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}
      >
        {isActive ? (
          <>
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            Active
          </>
        ) : (
          <>
            <XCircleIcon className="h-4 w-4 mr-1" />
            Inactive
          </>
        )}
      </span>
    );
  };

  const getReferralSourceName = () => {
    if (!customer?.referral_source_id) return 'N/A';
    const source = referralSources.find(
      (item) => Number(item.id) === Number(customer.referral_source_id)
    );
    return source?.name || 'N/A';
  };

  const getIndustryName = () => {
    if (!customer?.industry_id) return 'N/A';
    const industry = industries.find(
      (item) => Number(item.id) === Number(customer.industry_id)
    );
    return industry?.name || 'N/A';
  };

  const getAssignedContractName = () => {
    if (!customer?.assigned_contract_id) return 'N/A';
    const contract = assignedContracts.find(
      (item) => Number(item.id) === Number(customer.assigned_contract_id)
    );
    return contract?.name || 'N/A';
  };

  const getCurrencyName = () => {
    if (!customer?.default_currency_id) return 'N/A';
    const currency = currencies.find(
      (item) => Number(item.id) === Number(customer.default_currency_id)
    );
    return currency?.name || 'N/A';
  };

  const getTaxItemName = () => {
    if (!customer?.tax_item_id) return 'N/A';
    const taxItem = taxItems.find(
      (item) => Number(item.id) === Number(customer.tax_item_id)
    );
    return taxItem?.name || 'N/A';
  };

  const getPrefixName = (prefixId) => {
    if (!prefixId) return '';
    const prefix = prefixes.find(
      (item) => Number(item.id) === Number(prefixId)
    );
    return prefix?.name || '';
  };

  const getFrequencyName = (frequencyId) => {
    if (!frequencyId) return 'N/A';
    const freq = frequencies.find(
      (item) => Number(item.id) === Number(frequencyId)
    );
    return freq?.name || 'N/A';
  };

  const getAgentName = (agentId) => {
    if (!agentId) return 'N/A';
    const agent = assignedAgents.find(
      (item) => Number(item.id) === Number(agentId)
    );
    if (agent) return `${agent.first_name} ${agent.last_name}`;
    return 'N/A';
  };

  if (!isFetching && !customer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Customer Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The customer you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate(getRoutePath('/customers'))}
            className="inline-flex items-center px-4 py-2 bg-[#009efb] text-white rounded-lg hover:bg-[#0089db] transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Customers
          </button>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-3">
          <div className="h-8 w-8 border-2 border-[#009efb] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Loading customer information...</p>
        </div>
      </div>
    );
  }

  const primaryContact = customer.primary_contact || {};
  const serviceLocations = customer.service_locations || [];
  const serviceAgreements = customer.service_agreements || [];
  const customerAssignedAgents = customer.assigned_agents || [];
  const documents = customer.documents || [];
  const selectedDocs = customer.selected_docs || [];

  const tagChips = [];
  if (Array.isArray(customer.customer_tags)) {
    customer.customer_tags.forEach((tag) => {
      if (tag?.id && tag?.name) {
        tagChips.push({ id: tag.id, name: tag.name });
      }
    });
  }
  if (
    Array.isArray(customer.customer_tag_ids) &&
    Array.isArray(customerTags) &&
    customerTags.length
  ) {
    customer.customer_tag_ids.forEach((tagId) => {
      const found = customerTags.find(
        (tag) => Number(tag.id) === Number(tagId)
      );
      if (found) {
        tagChips.push({ id: found.id, name: found.name });
      }
    });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <Breadcrumb />
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mt-4 space-y-4 md:space-y-0">
            {/* Left side */}
            <div className="flex items-start md:items-center space-x-3">
              <div className="p-2 bg-[#009efb] rounded-lg">
                <UserIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {customer.name || 'Customer Details'}
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  {customer.account_number && (
                    <span>Account #{customer.account_number}</span>
                  )}
                  <span>{getCustomerStatusBadge()}</span>
                  {customer.is_vip_account && (
                    <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                      VIP Account
                    </span>
                  )}
                </div>
                {customer.parent_account && (
                  <p className="text-gray-500 mt-1 text-xs md:text-sm">
                    Parent Account:{' '}
                    <Link
                      to={getRoutePath(
                        `/customers/view/${customer.parent_account.id}`
                      )}
                      className="text-[#009efb] hover:text-[#0089db]"
                    >
                      {customer.parent_account.name}
                    </Link>
                  </p>
                )}
              </div>
            </div>

            {/* Right side buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:flex-wrap gap-2 w-full md:w-auto">
              <button
                onClick={() => navigate(getRoutePath('/customers'))}
                className="inline-flex items-center justify-center px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md bg-white hover:bg-gray-50 transition-colors w-full sm:w-auto"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to List
              </button>

              <button
                onClick={handleEdit}
                className="inline-flex items-center justify-center px-4 py-2 text-sm bg-[#009efb] text-white rounded-md hover:bg-[#0089db] transition-colors w-full sm:w-auto"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Customer
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="xl:col-span-2 space-y-6">
            {/* Account Info Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <BuildingOffice2Icon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Account Information
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Name
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {customer.name || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Number
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {customer.account_number || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Referral Source
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {getReferralSourceName()}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Industry
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {getIndustryName()}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assigned Contract
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {getAssignedContractName()}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Number/Tax ID
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {customer.tax_id || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Default Currency
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {getCurrencyName()}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tax Item
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {getTaxItemName()}
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={customer.is_taxable || false}
                      disabled
                      className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-700">
                      Taxable
                    </label>
                  </div>
                </div>

                {/* Tags */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <TagIcon className="h-4 w-4 mr-2" />
                    Customer Tags
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {tagChips.length > 0 ? (
                      tagChips.map((tag) => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag.name}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-gray-500">
                        No tags assigned
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Primary Contact Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <UserIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Primary Contact
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prefix
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {getPrefixName(primaryContact.prefix_id) || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {primaryContact.first_name || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {primaryContact.last_name || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Suffix
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {primaryContact.suffix || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Department
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {primaryContact.department || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Job Title
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {primaryContact.job_title || 'N/A'}
                    </div>
                  </div>
                </div>

                {/* Contact Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={primaryContact.is_billing_contact || false}
                      disabled
                      className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-700">
                      Billing Contact
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={primaryContact.is_booking_contact || false}
                      disabled
                      className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-700">
                      Booking Contact
                    </label>
                  </div>
                </div>

                {/* Personal Dates */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Birthday
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {primaryContact.birthday || 'N/A'}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Anniversary
                    </label>
                    <div className="p-3 bg-gray-50 rounded-lg text-gray-900">
                      {primaryContact.anniversary || 'N/A'}
                    </div>
                  </div>
                </div>

                {/* Phone Numbers */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <PhoneIcon className="h-4 w-4 mr-2" />
                    Phone Numbers
                  </label>
                  {(primaryContact.phone_numbers || []).length > 0 ? (
                    (primaryContact.phone_numbers || []).map((phone, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1">
                          <span className="text-xs uppercase tracking-wide text-gray-500">
                            {phone.type || 'Phone'}
                          </span>
                          <div className="text-gray-900">
                            {phone.number || 'N/A'}{' '}
                            {phone.ext && (
                              <span className="text-gray-500">
                                (Ext: {phone.ext})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No phone numbers</p>
                  )}
                </div>

                {/* Emails */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <EnvelopeIcon className="h-4 w-4 mr-2" />
                    Email Addresses
                  </label>
                  {(primaryContact.emails || []).length > 0 ? (
                    (primaryContact.emails || []).map((email, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1">
                          <span className="text-xs uppercase tracking-wide text-gray-500">
                            {email.type || 'Email'}
                          </span>
                          <div className="text-gray-900">
                            {email.address || 'N/A'}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No email addresses</p>
                  )}
                </div>
              </div>
            </div>

            {/* Service Locations Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <MapPinIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Service Locations
                  </h2>
                </div>
              </div>
              <div className="p-6">
                {serviceLocations.length > 0 ? (
                  serviceLocations.map((location, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-4 mb-4"
                    >
                      <h4 className="text-sm font-medium text-gray-700 mb-4">
                        Location {index + 1}
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Location Nickname
                          </label>
                          <div className="p-2 bg-gray-50 rounded-md">
                            <span className="text-gray-900">
                              {location.location_nickname || 'N/A'}
                            </span>
                          </div>
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Street Address
                          </label>
                          <div className="p-2 bg-gray-50 rounded-md">
                            <span className="text-gray-900">
                              {location.street_address || 'N/A'}
                            </span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Apt/Suite/Unit
                          </label>
                          <div className="p-2 bg-gray-50 rounded-md">
                            <span className="text-gray-900">
                              {location.apt || 'N/A'}
                            </span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            City
                          </label>
                          <div className="p-2 bg-gray-50 rounded-md">
                            <span className="text-gray-900">
                              {location.city || 'N/A'}
                            </span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            State
                          </label>
                          <div className="p-2 bg-gray-50 rounded-md">
                            <span className="text-gray-900">
                              {location.state || 'N/A'}
                            </span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            ZIP Code
                          </label>
                          <div className="p-2 bg-gray-50 rounded-md">
                            <span className="text-gray-900">
                              {location.zipcode || 'N/A'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={location.is_primary_location || false}
                            disabled
                            className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                          />
                          <label className="ml-2 block text-sm text-gray-700">
                            Primary Location
                          </label>
                        </div>

                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={location.is_billing_address || false}
                            disabled
                            className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                          />
                          <label className="ml-2 block text-sm text-gray-700">
                            Billing Address
                          </label>
                        </div>

                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={location.is_gated_property || false}
                            disabled
                            className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
                          />
                          <label className="ml-2 block text-sm text-gray-700">
                            Gated Property
                          </label>
                        </div>

                        {location.gate_access_instructions && (
                          <div className="md:col-span-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Gate Access Instructions
                            </label>
                            <div className="p-2 bg-gray-50 rounded-md">
                              <span className="text-gray-900">
                                {location.gate_access_instructions}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-6">
                    <MapPinIcon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      No service locations added yet.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Documents Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Documents
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-6">
                {(documents.length > 0 || selectedDocs.length > 0) ? (
                  <div className="space-y-4">
                    {documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-900 font-medium truncate">
                            {doc.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {doc.created_at}
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewFile(doc.url)}
                            className="text-[#009efb] hover:text-[#0089db]"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() =>
                              handleDownloadFile(doc.url, doc.name)
                            }
                            className="text-green-600 hover:text-green-700"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}

                    {selectedDocs.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-900 font-medium truncate">
                            {doc.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            Customer Document
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewFile(doc.url)}
                            className="text-[#009efb] hover:text-[#0089db]"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() =>
                              handleDownloadFile(doc.url, doc.name)
                            }
                            className="text-green-600 hover:text-green-700"
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                      <PhotoIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <div className="text-center">
                      <h3 className="text-lg font-medium text-gray-900 mb-1">
                        No Documents Uploaded
                      </h3>
                      <p className="text-sm text-gray-500">
                        Upload documents from the Edit Customer screen.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
             
            {/* Service Agreements Card */}
            {Boolean(customer.has_service_agreement) &&
               serviceAgreements.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                
                <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                  <div className="flex items-center">
                    <DocumentTextIcon className="h-5 w-5 text-white mr-3" />
                    <h2 className="text-xl font-semibold text-white">
                      Service Agreements
                    </h2>
                  </div>
                </div>
                <div className="p-6">
                  {serviceAgreements.map((agreement, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-4 mb-4 last:mb-0"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Agreement Name
                          </label>
                          <div className="p-2 bg-gray-50 rounded-md text-gray-900">
                            {agreement.name || 'N/A'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Effective Date
                          </label>
                          <div className="p-2 bg-gray-50 rounded-md text-gray-900">
                            {agreement.effective_date || 'N/A'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Expiration Date
                          </label>
                          <div className="p-2 bg-gray-50 rounded-md text-gray-900">
                            {agreement.expiration_date || 'N/A'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Amount $
                          </label>
                          <div className="p-2 bg-gray-50 rounded-md text-gray-900">
                            {agreement.amount || 'N/A'}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Frequency
                          </label>
                          <div className="p-2 bg-gray-50 rounded-md text-gray-900">
                            {getFrequencyName(agreement.frequency)}
                          </div>
                        </div>

                        {agreement.description && (
                          <div className="md:col-span-2 lg:col-span-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Description/Notes
                            </label>
                            <div className="p-2 bg-gray-50 rounded-md text-gray-900">
                              {agreement.description}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Notes
                  </h2>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Internal/Private Notes
                  </label>
                  <div className="p-3 bg-gray-50 rounded-lg text-gray-900 min-h-[60px] whitespace-pre-wrap">
                    {customer.private_notes || 'N/A'}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Public Work Order Notes
                  </label>
                  <div className="p-3 bg-gray-50 rounded-lg text-gray-900 min-h-[60px] whitespace-pre-wrap">
                    {customer.order_notes || 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            {/* Assigned Agents/Reps Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#009efb] to-[#0089db] px-6 py-3">
                <div className="flex items-center">
                  <UserIcon className="h-5 w-5 text-white mr-3" />
                  <h2 className="text-xl font-semibold text-white">
                    Assigned Agents/Reps
                  </h2>
                </div>
              </div>
              <div className="p-6">
                {customerAssignedAgents.length > 0 ? (
                  <div className="space-y-3">
                    {customerAssignedAgents.map((agent, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <span className="text-xs uppercase tracking-wide text-gray-500">
                              Agent/Rep
                            </span>
                            <div className="text-gray-900">
                              {getAgentName(agent.agent_rep_id)}
                            </div>
                          </div>
                          <div>
                            <span className="text-xs uppercase tracking-wide text-gray-500">
                              Commission Type
                            </span>
                            <div className="text-gray-900">
                              {agent.commission_type === '1' ? '%' : agent.commission_type === '2' ? '$' : 'N/A'}
                            </div>
                          </div>
                          <div>
                            <span className="text-xs uppercase tracking-wide text-gray-500">
                              Commission
                            </span>
                            <div className="text-gray-900">
                              {agent.commission || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <UserIcon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      No agents/reps assigned yet.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Summary */}
          <div className="space-y-6">
            {/* Status & Meta Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">
                  Account Summary
                </h2>
              </div>
              <div className="p-6 space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  {getCustomerStatusBadge()}
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">VIP Account</span>
                  <span className="font-medium text-gray-900">
                    {customer.is_vip_account ? 'Yes' : 'No'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Service Agreement</span>
                  <span className="font-medium text-gray-900">
                    {customer.has_service_agreement ? 'Yes' : 'No'}
                  </span>
                </div>

                {customer.formated_created_at && (
                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      Created At
                    </div>
                    <div className="text-sm text-gray-900">
                      {customer.formated_created_at}
                    </div>
                  </div>
                )}

                {customer.formated_updated_at && (
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      Last Updated
                    </div>
                    <div className="text-sm text-gray-900">
                      {customer.formated_updated_at}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerView;

